import { FC } from 'react';
import { Mail, MoveUpRight } from 'lucide-react';
import { socialMedia } from '@/data';
import { Button } from '../ui/button';

const chips = ['TypeScript • React • Next.js', 'Design Systems • Component APIs', 'TanStack Query • Zustand', 'React Hook Form • Zod • Stripe', 'SSR • SEO • Structured Data'];

const EMAIL = 'vadym.monastyrskyi@gmail.com';
const GMAIL_COMPOSE_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(EMAIL)}`;

export const Footer: FC = () => {
  return (
    <footer className="w-full pb-10 pt-8 md:pt-10 scroll-mt-28" id="contact">
      <div
        className="relative w-full overflow-hidden rounded-3xl border border-white/[0.1] p-6 sm:p-8 md:p-10 transition-colors duration-300 hover:border-white/[0.18]"
        style={{
          background: 'rgb(4,7,29)',
          backgroundImage: 'linear-gradient(90deg, rgba(4,7,29,1) 0%, rgba(12,14,35,1) 100%)',
        }}
      >
        {/* Background accents (match site language: subtle grid + vignette) */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-grid-small-white/[0.05]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(203,172,249,0.12)_0%,rgba(0,0,0,0)_45%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0)_0%,rgba(0,0,0,0.55)_70%,rgba(0,0,0,0.8)_100%)]" />
        </div>

        <div className="relative z-10">
          <p className="text-xs uppercase tracking-widest text-blue-100/80">
            Complex UI • Design systems • Performance
          </p>

          <h2 className="mt-3 text-3xl md:text-4xl font-semibold leading-tight">
            Let&apos;s build
            {' '}
            <span className="text-purple">fast, product-grade</span>
            {' '}
            UI.
          </h2>

          <p className="mt-3 text-white-200 max-w-2xl">
            I build complex interactive UI, design systems, and typed data layers - reusable primitives, form kits, and data infrastructure teams can build on.
          </p>

          {/* chips */}
          <div className="mt-5 flex flex-wrap gap-2">
            {chips.map((c) => (
              <span
                key={c}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-white/80 transition-colors duration-200 hover:border-purple/40 hover:bg-purple/10 hover:text-white"
              >
                {c}
              </span>
            ))}
          </div>

          {/* ctas + socials */}
          <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <a href="#projects" className="w-full sm:w-auto">
              <Button variant="magic" className="w-full sm:w-auto mt-0!">
                View projects
                {' '}
                <MoveUpRight className="ml-2 size-4" />
              </Button>
            </a>

            <div className="flex w-full flex-col gap-3 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:gap-4">
              <a
                href={GMAIL_COMPOSE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button variant="simple" className="w-full sm:w-auto mt-0!">
                  Email me
                  {' '}
                  <Mail className="ml-2 size-4" />
                </Button>
              </a>

              <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                {socialMedia.map((social) => (
                  <a
                    key={social.link}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.title}
                    title={social.title}
                    className="flex size-9 items-center justify-center rounded-full border border-white/20 bg-black/50 hover:bg-black transition sm:size-10"
                  >
                    <social.icon className="size-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Want more context on scope, impact, and tradeoffs? Happy to connect.
          </p>
        </div>

        {/* optional bottom line */}
        <div className="mt-8 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            ©
            {new Date().getFullYear()}
            {' '}
            Vadym Monastyrskyi
          </span>
          <span className="hidden sm:block">Public repos linked above • Some work is under NDA (limited details).</span>
        </div>
      </div>
    </footer>
  );
};
